import { FunctionComponent, useMemo, useState } from 'react';
import { Box, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { TooltipButton } from '../../miscellaneous-components/tooltip-button';
import gaiaTheme from '../../../../root-components/theme';
import { axisClasses } from '@mui/x-charts';
import { applyTopNWithOther, TopNValue } from '../../../../shared/utils/analytics-utils';
import { TopNSelect } from './top-n-select';

export const GraphBarChart: FunctionComponent<{
  title: string,
  tooltip?: string,
  dataset: Array<{}>,
  colors: string[],
  textColor?: string,
  series: Array<{ dataKey: string, label: string }>,
  dataKey: string,
  backgroundColor?: string,
  layout: 'vertical' | 'horizontal' | undefined,
  chartHeight?: number,
  paperPadding?: number,
  titleVariant?: 'body2' | 'subtitle1',
  paperHeight?: number | string,
  /** Extra room for long category labels on horizontal (y-axis) charts */
  horizontalLabelGutter?: number,
  /** Show a Top N dropdown that re-slices the dataset (with "Other") client-side. */
  enableTopN?: boolean,
  defaultTopN?: TopNValue,
  /**
   * Short line under the title, for when the chart's scope needs stating rather than hiding in the
   * tooltip (e.g. "this shows your selection only"). A tooltip needs a deliberate hover, so anything
   * a reader must know to interpret the bars correctly belongs here instead.
   */
  caption?: string,
  /** Hide the title/tooltip header row (e.g. when the caller renders its own fixed title). */
  hideHeader?: boolean,
  /** A control rendered in the header row before the Top N select, e.g. a toggle that switches what the bars break down by. */
  headerAction?: React.ReactNode,
  /**
   * Click-to-drill: fires with the clicked bar's data row (from the DISPLAYED, post-Top-N dataset)
   * and its index. The aggregate "Other" bar is never reported, since it is not a single entity.
   */
  onSelect?: (item: any, dataIndex: number) => void,
}> = ({
  title,
  tooltip,
  dataset,
  series,
  dataKey,
  backgroundColor = 'white',
  layout,
  colors,
  textColor = 'text.primary',
  chartHeight = 400,
  paperPadding = 4,
  titleVariant = 'subtitle1',
  paperHeight = '100%',
  horizontalLabelGutter,
  enableTopN = false,
  defaultTopN = 10,
  caption,
  hideHeader = false,
  headerAction,
  onSelect,
}) => {

    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
    const leftGutter = layout === 'horizontal'
      ? (horizontalLabelGutter ?? 220)
      : 60;

    const [topN, setTopN] = useState<TopNValue>(defaultTopN);
    const valueKey = series[0]?.dataKey ?? 'value';
    // Secondary series (a comparison period) must fold into "Other" alongside the primary.
    const extraKeys = useMemo(() => series.slice(1).map((s) => s.dataKey), [series]);
    const displayed = useMemo(
      () => (enableTopN ? applyTopNWithOther(dataset, topN, valueKey, extraKeys) : dataset),
      [enableTopN, dataset, topN, valueKey, extraKeys],
    );
    // Horizontal bars need ~one row of height each (a grouped pair needs more);
    // grow so Top 20 / All stays readable.
    const rowHeight = series.length > 1 ? 48 : 30;
    const effectiveHeight = enableTopN && layout === 'horizontal'
      ? Math.max(chartHeight, displayed.length * rowHeight + 40)
      : chartHeight;
    // A lone series keeps the brand colour; a comparison series renders in neutral grey so
    // the current period stays the figure and the comparison reads as reference.
    const seriesColors = series.length > 1
      ? [colors[0], gaiaTheme.palette.grey[500]]
      : [colors[0]];

    return (
      <Paper variant='outlined' sx={{ p: paperPadding, backgroundColor: backgroundColor, border: backgroundColor === 'primary.dark' ? 'none' : '', height: paperHeight, display: 'flex', flexDirection: 'column' }}>
        {!hideHeader && <Stack direction='row' alignItems={'center'} spacing={1}>
          <Typography variant={titleVariant} sx={{ flex: 1, color: textColor }}>
            <b>{title}</b>
          </Typography>
          {tooltip && !isMobileMode && <TooltipButton
            text={tooltip}
            noPadding={true}
            placement='right'
          />}
          {headerAction}
          {enableTopN && <TopNSelect value={topN} onChange={setTopN} />}
        </Stack>}
        {caption && (
          <Typography variant='caption' sx={{ color: 'text.secondary', mt: 0.25, mb: 0.5 }}>
            {caption}
          </Typography>
        )}
        {series.length > 1 && (
          <Stack direction='row' spacing={2} sx={{ mt: 0.5, mb: 0.5 }}>
            {series.map((entry, index) => (
              <Stack key={entry.dataKey} direction='row' alignItems='center' spacing={0.75}>
                <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: seriesColors[index], flexShrink: 0 }} />
                <Typography variant='caption' sx={{ color: textColor }}>{entry.label}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
        <Box sx={{ width: '100%', height: effectiveHeight }}>
          <BarChart
            dataset={displayed}
            xAxis={[{
              scaleType: layout === 'vertical' ? 'band' : undefined,
              dataKey: layout === 'vertical' ? dataKey : undefined,
              tickPlacement: layout === 'vertical' ? 'middle' : undefined,
              tickLabelPlacement: layout === 'vertical' ? 'middle' : undefined,
              tickSize: 0,
              disableLine: layout === 'vertical' ? false : true
            }]}
            yAxis={[{
              scaleType: layout === 'horizontal' ? 'band' : undefined,
              dataKey: layout === 'horizontal' ? dataKey : undefined,
              tickPlacement: layout === 'horizontal' ? 'middle' : undefined,
              tickLabelPlacement: layout === 'horizontal' ? 'middle' : undefined,
              tickSize: 0,
              disableLine: layout === 'vertical' ? true : false,
              tickLabelStyle: layout === 'horizontal' ? { fontSize: 12 } : undefined,
            }]}
            series={series}
            grid={{
              horizontal: layout === 'vertical' && backgroundColor === 'primary.dark' ? false :
                layout === 'vertical' ? true :
                  false,
              vertical: layout === 'horizontal' && backgroundColor === 'primary.dark' ? false :
                layout === 'horizontal' ? true : false
            }}
            slotProps={{
              legend: { hidden: true }
            }}
            margin={{
              left: leftGutter,
              right: 16,
              top: 8,
              bottom: layout === 'horizontal' ? 28 : 36,
            }}
            height={effectiveHeight}
            borderRadius={5}
            axisHighlight={{ x: 'none' }}
            layout={layout}
            colors={seriesColors}
            onItemClick={onSelect
              ? ((_event: any, bar: { dataIndex: number }) => {
                const clicked: any = displayed[bar.dataIndex];
                if (clicked && String(clicked.label) !== 'Other') onSelect(clicked, bar.dataIndex);
              })
              : undefined}
            sx={(theme) => ({
              ...(onSelect ? { '& .MuiBarElement-root': { cursor: 'pointer' } } : {}),
              [`.${axisClasses.root}`]: {
                [`.${axisClasses.tick}, .${axisClasses.line}`]: {
                  stroke: textColor,
                  strokeWidth: 1,
                },
                [`.${axisClasses.tickLabel}`]: {
                  fill: textColor,
                },
              },
            })}
          />
        </Box>
      </Paper>
    )
  }